import React, { useState } from 'react';
import { useSEO } from '../lib/useSEO';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { boardsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Sparkles, ArrowLeft, Loader2 } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png';

const DIFFICULTIES = [
  { value: 'college', label: 'College', description: 'Entry level - 60-80% correct rate' },
  { value: 'adult', label: 'Adult', description: 'Standard - 40-60% correct rate' },
  { value: 'masters', label: 'Masters', description: 'Expert - 15-35% correct rate' },
];

export default function BoardCreate() {
  useSEO('Create a Board | JepPrep', 'Generate a custom Jeopardy-style trivia board with AI-powered clue creation.');
  const navigate = useNavigate();
  const [difficulty, setDifficulty] = useState('adult');
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      toast.info('Generating board... This may take up to 90 seconds.');
      const response = await boardsAPI.generate({ difficulty });
      const board = response.data;

      if (board.generation_status === 'ready') {
        toast.success('Board generated successfully!');
        navigate(`/boards/${board.id}/edit`);
        return;
      }

      // Poll until ready
      const boardId = board.id;
      const pollInterval = setInterval(async () => {
        try {
          const pollRes = await boardsAPI.get(boardId);
          const status = pollRes.data.generation_status;
          if (status === 'ready') {
            clearInterval(pollInterval);
            setIsGenerating(false);
            toast.success('Board generated successfully!');
            navigate(`/boards/${boardId}/edit`);
          } else if (status === 'failed') {
            clearInterval(pollInterval);
            setIsGenerating(false);
            toast.error(pollRes.data.generation_error || 'Board generation failed. Please try again.');
          }
        } catch {
          clearInterval(pollInterval);
          setIsGenerating(false);
          toast.error('Lost connection while generating. Check your boards list.');
        }
      }, 3000);

    } catch (error) {
      console.error('Failed to generate board:', error);
      const errorMessage = error.response?.data?.detail || error.message;
      toast.error(errorMessage || 'Failed to generate board. Please try again.');
      setIsGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Navigation */}
      <nav className="border-b border-[hsl(var(--border))] bg-[hsl(var(--secondary))]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/boards">
                <Button variant="ghost" size="sm" className="text-gray-300">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <img src={LOGO_URL} alt="JepPrep" className="h-12" />
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="mx-auto max-w-2xl px-4 py-12 pb-24">
        <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
          <CardHeader className="text-center">
            <div className="w-16 h-16 mx-auto bg-[hsl(var(--brand-blue-800))] rounded-xl flex items-center justify-center mb-4">
              <Sparkles className="w-8 h-8 text-[hsl(var(--brand-gold-500))]" />
            </div>
            <CardTitle className="text-2xl font-display text-white">Create New Board</CardTitle>
            <CardDescription className="text-gray-400">
              Generate a complete game board with AI. Choose your difficulty level.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label className="text-gray-300">Difficulty Level</Label>
              <Select value={difficulty} onValueChange={setDifficulty}>
                <SelectTrigger className="bg-[hsl(var(--accent))] border-[hsl(var(--border))] text-white" data-testid="difficulty-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                  {DIFFICULTIES.map((d) => (
                    <SelectItem key={d.value} value={d.value} className="text-white">
                      <div>
                        <div className="font-medium">{d.label}</div>
                        <div className="text-xs text-gray-400">{d.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Difficulty Info */}
            <div className="bg-[hsl(var(--accent))] rounded-lg p-4">
              <h4 className="text-white font-medium mb-2">What you'll get:</h4>
              <ul className="text-sm text-gray-400 space-y-1">
                <li>• 6 categories with 5 clues each (30 total)</li>
                <li>• Values: $100-$500 for Round 1, $200-$1000 for Round 2</li>
                <li>• 1-2 Daily Doubles per round</li>
                <li>• Final question</li>
                <li>• AI-generated clues tailored to your difficulty</li>
              </ul>
            </div>

            <Button
              onClick={handleGenerate}
              disabled={isGenerating}
              className="w-full bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white py-6 text-lg"
              data-testid="generate-board-button"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Generating Board...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  <span className="text-sm sm:text-base">Generate Board with AI</span>
                </>
              )}
            </Button>

            {isGenerating && (
              <div className="text-center space-y-2">
                <div className="text-gray-400 text-sm">
                  Please wait while AI creates your board. This usually takes 30-60 seconds.
                </div>
                <div className="text-gray-500 text-xs">
                  The AI is generating 6 categories with 5 clues each, plus Final Jeopardy...
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
